import { Box, Chip, Divider, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useRevealWage } from "../../../hooks/useRevealWage";
import { ALERT_CORAL, INK, NEUTRAL, STATUS_BADGE } from "../../../theme/tokens";
import type { DayStats, NurseFullRole, StatsCalculationParams } from "../../../utils/models";
import { calculateDayStats } from "../../../utils/statsCalculations";
import { formatNumber, getChipColor } from "../../../utils/utilFunctions";
import ColorfulRole from "../ColorfulRole/ColorfulRole";

type Props = {
	date: DateTime;
	shiftIndex: number;
};

/**
 * Component to display statistics for a specific shift on a given date.
 * @param date - The date for which the shift statistics are displayed.
 * @param shiftIndex Day(0) or Night(1) shift index.
 */
export const ShiftStats: FC<Props> = ({ date, shiftIndex }) => {
	const { hospitalData, getWeekIndexByDate, timezone, chosenDepartment, departmentNurseTypes, federalHolidaysUSASet, isHospital, wageData } = useHospitalData();
	const canViewWage = useRevealWage();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const weekIndex = useMemo(() => getWeekIndexByDate(date), [getWeekIndexByDate, date]);
	const dayStats = useMemo<DayStats | undefined>(() => {
		if (!hospitalData?.weeks?.[weekIndex]?.weeklyShifts || isHospital) return;

		const params: StatsCalculationParams = {
			weekIndex,
			weeklyShifts: hospitalData.weeks[weekIndex].weeklyShifts,
			nurses: hospitalData.nurses,
			dayIndex: date.weekday % 7,
			department: chosenDepartment,
			timezone: timezone,
			rolesToDisplay: departmentNurseTypes,
			hospitalData: hospitalData,
			federalHolidaysUSASet,
			jobManagementEnabled,
			wageData,
		};

		// Match the weekly view's SNF arg (WeekViewProvider passes !isHospital, not isSnf) so an
		// ALF department applies the same role-filter behavior as the desktop grid.
		return calculateDayStats(params, !isHospital, timezone);
	}, [hospitalData, weekIndex, date, chosenDepartment, departmentNurseTypes, timezone, federalHolidaysUSASet, isHospital, jobManagementEnabled, wageData]);

	const chipColor = useMemo(() => {
		if (!dayStats) {
			return { backgroundColor: ALERT_CORAL, color: `${alpha(INK.SECONDARY, 1)}` };
		}

		return getChipColor(dayStats, chosenDepartment);
	}, [dayStats, chosenDepartment]);

	const roleCounters = useMemo(() => {
		const counters: Record<string, number> = {};

		if (date && weekIndex) {
			const nursesInShift = hospitalData.weeks[weekIndex].weeklyShifts[shiftIndex].days[date.weekday % 7].nurses;

			nursesInShift.forEach((nurse) => {
				counters[nurse.role] = (counters[nurse.role] || 0) + 1;
			});
		}

		return counters;
	}, [hospitalData, weekIndex, date, shiftIndex]);

	return (
		<Box
			data-testid="shift-stats-container"
			sx={{
				width: "100%",
				bgcolor: NEUTRAL[50],
				p: 1,
			}}
		>
			<Box sx={{ mb: 1 }}>
				<Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
					<Chip
						label={`${dayStats ? formatNumber((dayStats.totalHours / dayStats.wantedHours) * 100, 0) : 0}%`}
						sx={{
							backgroundColor: chipColor.backgroundColor,
							color: chipColor.color,
							fontWeight: 600,
							fontSize: "16px",
							borderRadius: "20px",
						}}
					/>
				</Box>

				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						gap: 4,
					}}
				>
					<Box sx={{ display: "flex", alignItems: "baseline" }}>
						<Typography sx={{ color: STATUS_BADGE.BRAND_FALLBACK, fontSize: "14px", mr: 1 }}>$PPD</Typography>
						<Typography sx={{ color: INK.SECONDARY, fontWeight: 600, fontSize: "14px" }}>${!canViewWage ? "0.0" : dayStats?.ppd}</Typography>
					</Box>
					<Box sx={{ display: "flex", alignItems: "baseline" }}>
						<Typography sx={{ color: STATUS_BADGE.BRAND_FALLBACK, fontSize: "14px", mr: 1 }}>HPPD</Typography>
						<Typography sx={{ color: INK.SECONDARY, fontWeight: 600, fontSize: "14px" }}>{dayStats?.hppd}</Typography>
					</Box>
				</Box>
			</Box>
			<Divider sx={{ mb: 2, marginInline: 2 }} />
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					gap: 2,
					maxWidth: "100%",
					flexWrap: "wrap",
				}}
			>
				{roleCounters &&
					Object.keys(roleCounters).map((roleKey: string) => (
						<Box key={roleKey} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<ColorfulRole role={roleKey as NurseFullRole} />
							<Typography sx={{ color: INK.SECONDARY, fontWeight: 500, fontSize: "14px" }}>{roleCounters[roleKey as NurseFullRole]}</Typography>
						</Box>
					))}
			</Box>
		</Box>
	);
};
